import { useCallback, useContext, useEffect, useState } from "react"
import { SamplesContext } from "../../../context/samplesContext"
import { useNavigate } from "react-router-dom"
import { buildPathWithSampleId, PATH_INFO } from "../../utils/helper"
import { BlackButton } from "../../../components/buttons/BlackButton"
import paperIcon from '../../../assets/images/icons/paper-white.svg'
import importIcon from '../../../assets/images/icons/import.svg'
import downloadIcon from '../../../assets/images/icons/download.svg'
import ecImageDownloadIcon from '../../../assets/images/icons/t-shirt-down-arrow.svg'
import pastMeasurementIcon from '../../../assets/images/icons/paper-plus.svg'
import { DESKTOP_XMD_BREAKPOINT_PX, MEDIA_QUERY_MAX_DESKTOP_XMD } from "../../../constants/breakpoints" // Tailwindのdesktop-xmdと同期させるためconstantsで管理している値を使用
import { UserContext } from "../../../context/userContext"
import { MeasurementReportDownloadErrorModal } from "../utils/MeasurementReportDownloadErrorModal"
import { downloadMeasurementReport } from "../utils/measurementReportDownload"
import { buildAnnotatedImages } from "../../Specification-download/helpers"
import { useTableContext } from "../../../context/tableContext"
import { useTranslation } from "react-i18next"

export function ActionButtonsPanel() {
  const { t, i18n } = useTranslation()
  const { rowSample, canvasImage } = useContext(SamplesContext)
  const { isAuth, isGuest } = useContext(UserContext)
  const { getColor, getDisplayIndex } = useTableContext()
  const canEdit = Boolean(isAuth) && !isGuest

  const [isSmall, setIsSmall] = useState(false)
  const [downloadError, setDownloadError] = useState("")
  const [isDownloadErrorModalOpen, setIsDownloadErrorModalOpen] = useState(false)

  const navigate = useNavigate()
  const closeDownloadErrorModal = useCallback(() => {
    setIsDownloadErrorModalOpen(false)
    setDownloadError("")
  }, [])

  const downloadPdf = useCallback(async () => {
    let sampleImages: string[] = []
    try {
      sampleImages = await buildAnnotatedImages(rowSample, getColor, getDisplayIndex)
    } catch (imageError) {
      console.error("採寸画像の描画に失敗しました。", imageError)
    }

    const errorMessage = await downloadMeasurementReport({
      sampleId: rowSample.id,
      sampleUrl: rowSample.url ?? undefined,
      sampleImage: canvasImage,
      sampleImages,
      navigate
    })
    if (errorMessage) {
      setDownloadError(errorMessage)
      setIsDownloadErrorModalOpen(true)
    }
  }, [navigate, rowSample, canvasImage, getColor, getDisplayIndex])

  // desktop-xmd未満(タブレットレイアウト時)にボタンの文言を変更(ダウンロード → Download)
  useEffect(() => {
    const media = window.matchMedia(MEDIA_QUERY_MAX_DESKTOP_XMD) // desktop-xmd未満（tailwind.config.jsの値と同じ）
    const listener = () => setIsSmall(media.matches)

    listener()
    media.addEventListener('change', listener)

    return () => media.removeEventListener('change', listener)
  }, [])

  return (
    <div className="text-white space-y-[5px] desktop-xmd:mt-[25px] desktop-xmd:space-y-[10px]">
      <div className="grid grid-cols-2 gap-[5px] desktop-xmd:gap-[9px]">
        <BlackButton
          label={t("actions.showPastMeasurements")}
          subLabelParts={[t("actions.showPastMeasurementsDescription"), ""]}
          onClickHandle={() => navigate(buildPathWithSampleId(PATH_INFO.PAST_MEASUREMENTS.PATH, rowSample.id))}
          iconSrc={paperIcon}
          iconHeight="21"
          iconWidth="21"
          canEdit={canEdit}
        />
        <BlackButton
          label={t("actions.importSpecification")}
          subLabelParts={[t("actions.importSpecificationDescription"), ""]}
          subLabelBreakpointPx={DESKTOP_XMD_BREAKPOINT_PX}
          onClickHandle={() => navigate(buildPathWithSampleId(PATH_INFO.SPECIFICATION_INPORT.PATH, rowSample.id))}
          iconSrc={importIcon}
          iconHeight="21"
          iconWidth="21"
          canEdit={canEdit}
        />
      </div>
      <div className="grid grid-cols-2 gap-[5px] desktop-xmd:gap-[9px]">
        <BlackButton
          label={isSmall && i18n.language.startsWith("en") ? t("common.download") : t("actions.downloadMeasurementReport")}
          subLabelParts={[t("actions.downloadMeasurementReportDescription"), ""]}
          subLabelBreakpointPx={DESKTOP_XMD_BREAKPOINT_PX}
          onClickHandle={downloadPdf}
          iconSrc={downloadIcon}
          iconHeight="21"
          iconWidth="21"
          canEdit={canEdit}
        />
        <BlackButton
          label={isSmall && i18n.language.startsWith("en") ? t("common.download") : t("actions.downloadEcImages")}
          subLabelParts={[t("actions.downloadEcImagesDescription"), ""]}
          subLabelBreakpointPx={DESKTOP_XMD_BREAKPOINT_PX}
          onClickHandle={() => { navigate(buildPathWithSampleId(PATH_INFO.EC_PHOTO.PATH, rowSample.id)) }}
          iconSrc={ecImageDownloadIcon}
          iconHeight="21"
          iconWidth="21"
          canEdit={canEdit}
        />
      </div>
      <BlackButton
        label={t("actions.downloadPastMeasurementReport")}
        subLabelParts={[t("actions.downloadPastMeasurementReportDescription"), ""]}
        onClickHandle={() => { navigate(buildPathWithSampleId(PATH_INFO.SPECIFICATION_DOWNLOAD.PATH, rowSample.id)) }}
        iconSrc={pastMeasurementIcon}
        iconHeight="21"
        iconWidth="21"
        canEdit={canEdit}
      />
      <MeasurementReportDownloadErrorModal
        isOpen={isDownloadErrorModalOpen && Boolean(downloadError)}
        onClose={closeDownloadErrorModal}
        message={downloadError}
      />
    </div>
  )
}
